import * as React from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, isAuthenticated, homeForUser, useAuth } from "@/lib/auth";

/**
 * Route guard global : si l'utilisateur n'est pas authentifié, on le redirige
 * vers "/" (page login). Sinon les pages protégées rendent avec data=undefined
 * → toutes les valeurs affichent 0 (UX confusante).
 *
 * Déjà loggé sur "/" → on saute la page login vers la HOME DU RÔLE
 * (homeForUser), et non "/dashboard" : cette route dépend de la section
 * « general », absente de 7 rôles sur 11 → l'utilisateur revenait sur un 403
 * immédiatement après un login réussi.
 */
function RouteGuard({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    if (location !== "/" && !isAuthenticated()) {
      setLocation("/");
    } else if (location === "/" && isAuthenticated()) {
      setLocation(homeForUser(user));
    }
  }, [location, setLocation, user]);
  return <>{children}</>;
}
import NotFound from "@/pages/not-found";
import PagesRoadmap from "@/components/layout/pages-roadmap";
// P2 (2026-07-07) — garde de route par section : deep-link vers une section
// hors du rôle → page 403 honnête (mêmes règles que le filtrage de la nav).
import SectionGuard from "@/components/layout/section-guard";

const BudgetPrevisionnel = React.lazy(() => import("@/pages/budget-previsionnel"));
const BilanResultat = React.lazy(() => import("@/pages/bilan-resultat"));
const BalanceGenerale = React.lazy(() => import("@/pages/balance-generale"));
const GrandLivre = React.lazy(() => import("@/pages/grand-livre"));
const PlanComptable = React.lazy(() => import("@/pages/plan-comptable"));
const RapprochementBancaire = React.lazy(() => import("@/pages/rapprochement-bancaire"));
const LoginPage = React.lazy(() => import("@/pages/login"));
const DashboardPage = React.lazy(() => import("@/pages/dashboard"));

/**
 * 03/11/2026 (formation de l'économe général) : le tableau de bord diocésain mêlait des
 * chiffres de natures différentes et le PO s'y perdait. Pour l'Économat diocésain
 * (section « l'essentiel »), /dashboard ouvre « Flux financiers » ; les autres profils
 * gardent le tableau de bord.
 */
function TableauDeBordOuFlux() {
  const { hasSection } = useAuth();
  if (hasSection("essentiel")) return <Redirect to="/flux" />;
  return <DashboardPage />;
}
const TresoreriePage = React.lazy(() => import("@/pages/tresorerie"));
const SanteParoissesPage = React.lazy(() => import("@/pages/sante-paroisses"));
const ChequesPage = React.lazy(() => import("@/pages/cheques"));
const RentabiliteDirectionsPage = React.lazy(() => import("@/pages/rentabilite-directions"));
const PerequationPage = React.lazy(() => import("@/pages/perequation"));
const ComptabilitePage = React.lazy(() => import("@/pages/comptabilite"));
const CartographiePage = React.lazy(() => import("@/pages/cartographie"));
const BenchmarkingPage = React.lazy(() => import("@/pages/benchmarking"));
const ClergePage = React.lazy(() => import("@/pages/clerge"));
const ClergeAnnuaire = React.lazy(() => import("@/pages/clerge-annuaire"));
const ClergeFiche = React.lazy(() => import("@/pages/clerge-fiche"));
const ClergeAffectations = React.lazy(() => import("@/pages/clerge-affectations"));
const ClergeParcours = React.lazy(() => import("@/pages/clerge-parcours"));
const PersonnelLaic = React.lazy(() => import("@/pages/personnel-laic"));
const FormationRetraites = React.lazy(() => import("@/pages/formation-retraites"));
const ConformitePage = React.lazy(() => import("@/pages/conformite"));
const ProjetsPage = React.lazy(() => import("@/pages/projets"));
const ProjetsDpiPage = React.lazy(() => import("@/pages/projets-dpi"));
const ReversementsPage = React.lazy(() => import("@/pages/reversements"));
const CaisseVersementsPage = React.lazy(() => import("@/pages/caisse-versements"));
const ParametrageComptablePage = React.lazy(() => import("@/pages/parametrage-comptable"));
// Boucle financière (21/10/2026) : comptes de dépôt des paroisses (D-L),
// contrôles comptables, pièces à valider et export du journal.
const ComptesDepotParoissesPage = React.lazy(() => import("@/pages/comptes-depot-paroisses"));
const ControlesComptablesPage = React.lazy(() => import("@/pages/controles-comptables"));
const PiecesComptablesPage = React.lazy(() => import("@/pages/pieces-comptables"));
const PersonnelDiocesainPage = React.lazy(() => import("@/pages/personnel-diocesain"));
const ObservatoirePage = React.lazy(() => import("@/pages/observatoire"));
const AnalyticsPage = React.lazy(() => import("@/pages/analytics"));
const DecisionsPage = React.lazy(() => import("@/pages/decisions"));
const CommunicationPage = React.lazy(() => import("@/pages/communication"));
const KitQrPage = React.lazy(() => import("@/pages/kit-qr"));
const KitQrEcolesPage = React.lazy(() => import("@/pages/kit-qr-ecoles"));
const PatrimoinePage = React.lazy(() => import("@/pages/patrimoine"));
const PatrimoineInventaire = React.lazy(() => import("@/pages/patrimoine-inventaire"));
const PatrimoineFiche = React.lazy(() => import("@/pages/patrimoine-fiche"));
const PatrimoineTravaux = React.lazy(() => import("@/pages/patrimoine-travaux"));
const PatrimoineAssurances = React.lazy(() => import("@/pages/patrimoine-assurances"));
const PatrimoineContrats = React.lazy(() => import("@/pages/patrimoine-contrats"));
const SupportPage = React.lazy(() => import("@/pages/support"));
const ParoissesPage = React.lazy(() => import("@/pages/paroisses"));
const ParoissesApplicationPage = React.lazy(() => import("@/pages/paroisses-application"));
const ParoisseDetailPage = React.lazy(() => import("@/pages/paroisse-detail"));
const ParoisseFormPage = React.lazy(() => import("@/pages/paroisse-form"));
const ParoisseDashboardPage = React.lazy(() => import("@/pages/paroisse-dashboard"));
const ClotureHebdoPage = React.lazy(() => import("@/pages/cloture-hebdo"));
const JournalTransactionsPage = React.lazy(() => import("@/pages/journal-transactions"));
const TransactionDetailPage = React.lazy(() => import("@/pages/transaction-detail"));
const TransactionFormPage = React.lazy(() => import("@/pages/transaction-form"));
const ReconciliationPage = React.lazy(() => import("@/pages/reconciliation"));
const MobileMoneyIntegrations = React.lazy(() => import("@/pages/integrations-mobile-money"));
const ApiIntegrations = React.lazy(() => import("@/pages/integrations-api"));
const WebhookIntegrations = React.lazy(() => import("@/pages/integrations-webhooks"));
const CathoMediaDashboard = React.lazy(() => import("@/pages/catho-media-dashboard"));
const CathoMediaPublications = React.lazy(() => import("@/pages/catho-media-publications"));
const CathoMediaStats = React.lazy(() => import("@/pages/catho-media-stats"));
const AdminUtilisateurs = React.lazy(() => import("@/pages/admin-utilisateurs"));
const AdminArchivesLegales = React.lazy(() => import("@/pages/admin-archives-legales"));
const AdminUtilisateurDetail = React.lazy(() => import("@/pages/admin-utilisateur-detail"));
const AdminRoles = React.lazy(() => import("@/pages/admin-roles"));
const AdminAudit = React.lazy(() => import("@/pages/admin-audit"));
const AdminParametres = React.lazy(() => import("@/pages/admin-parametres"));
const AdminVicariats = React.lazy(() => import("@/pages/admin-vicariats"));
const AdminDoyennes = React.lazy(() => import("@/pages/admin-doyennes"));
const AdminFeatureFlags = React.lazy(() => import("@/pages/admin-feature-flags"));
const AdminDocuments = React.lazy(() => import("@/pages/admin-documents"));
const AdminContributions = React.lazy(() => import("@/pages/admin-contributions"));
const AdminDashboardDiocese = React.lazy(() => import("@/pages/admin-dashboard-diocese"));
const AdminEvenements = React.lazy(() => import("@/pages/admin-evenements"));
const AdminAnnonces = React.lazy(() => import("@/pages/admin-annonces"));
const AdminRadioProgrammes = React.lazy(() => import("@/pages/admin-radio-programmes"));
const AdminSemaineDiocesaine = React.lazy(() => import("@/pages/admin-semaine-diocesaine"));
const AdminPelerinages = React.lazy(() => import("@/pages/admin-pelerinages"));
const AdminCollectes = React.lazy(() => import("@/pages/admin-collectes"));
const AdminSolidariteProjets = React.lazy(() => import("@/pages/admin-solidarite-projets"));
const AdminActualites = React.lazy(() => import("@/pages/admin-actualites"));
const AdminActualitesEditeur = React.lazy(() => import("@/pages/admin-actualites-editeur"));
const AdminNewsletter = React.lazy(() => import("@/pages/admin-newsletter"));
const AdminCalendrierEditorial = React.lazy(() => import("@/pages/admin-calendrier-editorial"));
const CollectesRapports = React.lazy(() => import("@/pages/collectes-rapports"));
const FDSDashboard = React.lazy(() => import("@/pages/fds-dashboard"));
const FDSParoisses = React.lazy(() => import("@/pages/fds-paroisses"));
const FDSObjectifs = React.lazy(() => import("@/pages/fds-objectifs"));
const DenierCulte = React.lazy(() => import("@/pages/denier-culte"));
const IntentionsMesse = React.lazy(() => import("@/pages/intentions-messe"));
const CathoAppDashboard = React.lazy(() => import("@/pages/catho-app-dashboard"));
const CathoAppUsers = React.lazy(() => import("@/pages/catho-app-utilisateurs"));
const CathoAppContenus = React.lazy(() => import("@/pages/catho-app-contenus"));
const IntegrationsSync = React.lazy(() => import("@/pages/integrations-sync"));
const IntegrationsBackup = React.lazy(() => import("@/pages/integrations-backup"));
const AideDocumentation = React.lazy(() => import("@/pages/aide-documentation"));
const VisitesPastorales = React.lazy(() => import("@/pages/visites-pastorales"));
const FormationDiocesaine = React.lazy(() => import("@/pages/formation-diocesaine"));
const RapportAnnuel = React.lazy(() => import("@/pages/rapport-annuel"));
const RapportCanonique = React.lazy(() => import("@/pages/rapport-canonique"));
const RapportsPage = React.lazy(() => import("@/pages/rapports"));
const RapportsFinanciersPage = React.lazy(() => import("@/pages/rapports-financiers"));
const RapportsPastoraux = React.lazy(() => import("@/pages/rapports-pastoraux"));
const RapportsExport = React.lazy(() => import("@/pages/rapports-export"));
const TableauPersonnalise = React.lazy(() => import("@/pages/tableau-personnalise"));
const CalendrierLiturgique = React.lazy(() => import("@/pages/calendrier-liturgique"));
const Sacrements = React.lazy(() => import("@/pages/sacrements"));
const QuetesSpeciales = React.lazy(() => import("@/pages/quetes-speciales"));
// Suivi des dons de l'EDA et son paramétrage des accès (20/10/2026).
const SuiviDons = React.lazy(() => import("@/pages/suivi-dons"));
const RapprochementJeko = React.lazy(() => import("@/pages/rapprochement-jeko"));
const FluxFinanciers = React.lazy(() => import("@/pages/flux-financiers"));
const SuiviDonsAcces = React.lazy(() => import("@/pages/suivi-dons-acces"));
const DonsNature = React.lazy(() => import("@/pages/dons-nature"));
const MonCompte = React.lazy(() => import("@/pages/mon-compte"));
const NotificationsPage = React.lazy(() => import("@/pages/notifications"));
const CalendrierEcheances = React.lazy(() => import("@/pages/calendrier-echeances"));
const FournisseursPage = React.lazy(() => import("@/pages/fournisseurs"));
const FacturesAchatsPage = React.lazy(() => import("@/pages/factures-achats"));
const GestionDettePage = React.lazy(() => import("@/pages/gestion-dette"));
const MessageriePage = React.lazy(() => import("@/pages/messagerie"));
const SedecPilotageDashboard = React.lazy(() => import("@/pages/sedec-pilotage-dashboard"));
const SedecPilotageCarte = React.lazy(() => import("@/pages/sedec-pilotage-carte"));
const SedecPilotageEffectifs = React.lazy(() => import("@/pages/sedec-pilotage-effectifs"));
const SedecPilotageInscriptions = React.lazy(() => import("@/pages/sedec-pilotage-inscriptions"));
const SedecPilotagePerformance = React.lazy(() => import("@/pages/sedec-pilotage-performance"));
const SedecPilotageQualite = React.lazy(() => import("@/pages/sedec-pilotage-qualite"));
const SedecPilotageGouvernance = React.lazy(() => import("@/pages/sedec-pilotage-gouvernance"));
const SedecPilotageProjetEducatif = React.lazy(() => import("@/pages/sedec-pilotage-projet-educatif"));
const SedecPilotageInnovation = React.lazy(() => import("@/pages/sedec-pilotage-innovation"));
const SedecPilotageVeille = React.lazy(() => import("@/pages/sedec-pilotage-veille"));
const SedecPilotageMena = React.lazy(() => import("@/pages/sedec-pilotage-mena"));
const SedecPilotageStats = React.lazy(() => import("@/pages/sedec-pilotage-stats"));
const SedecPilotageProspective = React.lazy(() => import("@/pages/sedec-pilotage-prospective"));
const SedecPilotageRayonnement = React.lazy(() => import("@/pages/sedec-pilotage-rayonnement"));
const SedecPilotageImpactSocial = React.lazy(() => import("@/pages/sedec-pilotage-impact-social"));
const SedecFinancesConsolidation = React.lazy(() => import("@/pages/sedec-finances-consolidation"));
const SedecFinancesRevenus = React.lazy(() => import("@/pages/sedec-finances-revenus"));
const SedecFinancesPaiements = React.lazy(() => import("@/pages/sedec-finances-paiements"));
const SedecFinancesImpayes = React.lazy(() => import("@/pages/sedec-finances-impayes"));
const SedecFinancesFrais = React.lazy(() => import("@/pages/sedec-finances-frais"));
const SedecFinancesBourses = React.lazy(() => import("@/pages/sedec-finances-bourses"));
const SedecFinancesPerequation = React.lazy(() => import("@/pages/sedec-finances-perequation"));
const SedecFinancesBudgets = React.lazy(() => import("@/pages/sedec-finances-budgets"));
const SedecFinancesDepenses = React.lazy(() => import("@/pages/sedec-finances-depenses"));
const SedecFinancesInvestissements = React.lazy(() => import("@/pages/sedec-finances-investissements"));
const SedecFinancesAnalytique = React.lazy(() => import("@/pages/sedec-finances-analytique"));
const SedecFinancesControle = React.lazy(() => import("@/pages/sedec-finances-controle"));
const SedecFinancesAchatsGroupes = React.lazy(() => import("@/pages/sedec-finances-achats-groupes"));
const SedecFinancesSubventions = React.lazy(() => import("@/pages/sedec-finances-subventions"));
const SedecFinancesTresorerie = React.lazy(() => import("@/pages/sedec-finances-tresorerie"));
const SedecFinancesAssurances = React.lazy(() => import("@/pages/sedec-finances-assurances"));
const SedecFinancesRentabilite = React.lazy(() => import("@/pages/sedec-finances-rentabilite"));
const SedecFinancesTransparence = React.lazy(() => import("@/pages/sedec-finances-transparence"));
const SedecFinancesPredictions = React.lazy(() => import("@/pages/sedec-finances-predictions"));
const SedecFinancesRapports = React.lazy(() => import("@/pages/sedec-finances-rapports"));
const SedecOperationsInscriptionsGlobales = React.lazy(() => import("@/pages/sedec-operations-inscriptions-globales"));
const SedecOperationsMatricules = React.lazy(() => import("@/pages/sedec-operations-matricules"));
const SedecOperationsAffectations = React.lazy(() => import("@/pages/sedec-operations-affectations"));
const SedecOperationsParcours = React.lazy(() => import("@/pages/sedec-operations-parcours"));
const SedecOperationsTransferts = React.lazy(() => import("@/pages/sedec-operations-transferts"));
const SedecOperationsBoursesMerite = React.lazy(() => import("@/pages/sedec-operations-bourses-merite"));
const SedecOperationsAssuranceEleves = React.lazy(() => import("@/pages/sedec-operations-assurance-eleves"));
const SedecOperationsTransport = React.lazy(() => import("@/pages/sedec-operations-transport"));
const SedecOperationsCantine = React.lazy(() => import("@/pages/sedec-operations-cantine"));
const SedecOperationsActivites = React.lazy(() => import("@/pages/sedec-operations-activites"));
const SedecOperationsSante = React.lazy(() => import("@/pages/sedec-operations-sante"));
const SedecOperationsDiscipline = React.lazy(() => import("@/pages/sedec-operations-discipline"));
const SedecOperationsOrientation = React.lazy(() => import("@/pages/sedec-operations-orientation"));
const SedecOperationsExamens = React.lazy(() => import("@/pages/sedec-operations-examens"));
const SedecOperationsCommunication = React.lazy(() => import("@/pages/sedec-operations-communication"));
const SedecRhAnnuaire = React.lazy(() => import("@/pages/sedec-rh-annuaire"));
const SedecRhCarrieres = React.lazy(() => import("@/pages/sedec-rh-carrieres"));
const SedecRhFormation = React.lazy(() => import("@/pages/sedec-rh-formation"));
const SedecRhRecrutement = React.lazy(() => import("@/pages/sedec-rh-recrutement"));
const SedecRhEvaluations = React.lazy(() => import("@/pages/sedec-rh-evaluations"));
const SedecRhMobilite = React.lazy(() => import("@/pages/sedec-rh-mobilite"));
const SedecRhConventions = React.lazy(() => import("@/pages/sedec-rh-conventions"));
const SedecRhProtectionSociale = React.lazy(() => import("@/pages/sedec-rh-protection-sociale"));
const SedecRhClimatSocial = React.lazy(() => import("@/pages/sedec-rh-climat-social"));
const SedecRhCompetences = React.lazy(() => import("@/pages/sedec-rh-competences"));
const SedecRhSynergies = React.lazy(() => import("@/pages/sedec-rh-synergies"));
const SedecRhReconnaissance = React.lazy(() => import("@/pages/sedec-rh-reconnaissance"));
const SedecRhProspective = React.lazy(() => import("@/pages/sedec-rh-prospective"));
const SedecRhMedecine = React.lazy(() => import("@/pages/sedec-rh-medecine"));
const SedecRhConflits = React.lazy(() => import("@/pages/sedec-rh-conflits"));
const SedecPastoralProjet = React.lazy(() => import("@/pages/sedec-pastoral-projet"));
const SedecPastoralAumoneries = React.lazy(() => import("@/pages/sedec-pastoral-aumoneries"));
const SedecPastoralCatechese = React.lazy(() => import("@/pages/sedec-pastoral-catechese"));
const SedecPastoralSacrements = React.lazy(() => import("@/pages/sedec-pastoral-sacrements"));
const SedecPastoralMouvements = React.lazy(() => import("@/pages/sedec-pastoral-mouvements"));
const SedecPastoralCaritas = React.lazy(() => import("@/pages/sedec-pastoral-caritas"));
const SedecPastoralRetraites = React.lazy(() => import("@/pages/sedec-pastoral-retraites"));
const SedecPastoralFormationHumaine = React.lazy(() => import("@/pages/sedec-pastoral-formation-humaine"));
const SedecPastoralVocations = React.lazy(() => import("@/pages/sedec-pastoral-vocations"));
const SedecPastoralCelebrations = React.lazy(() => import("@/pages/sedec-pastoral-celebrations"));
const SedecPastoralRessources = React.lazy(() => import("@/pages/sedec-pastoral-ressources"));
const SedecPastoralAnimateurs = React.lazy(() => import("@/pages/sedec-pastoral-animateurs"));
const SedecPastoralImpact = React.lazy(() => import("@/pages/sedec-pastoral-impact"));
const SedecPastoralSynergie = React.lazy(() => import("@/pages/sedec-pastoral-synergie"));
const SedecPastoralRayonnement = React.lazy(() => import("@/pages/sedec-pastoral-rayonnement"));
const PaymentDashboard = React.lazy(() => import("@/pages/admin/payment/payment-dashboard"));
const PaymentProviders = React.lazy(() => import("@/pages/admin/payment/payment-providers"));
const PaymentOperators = React.lazy(() => import("@/pages/admin/payment/payment-operators"));
const PaymentCauses = React.lazy(() => import("@/pages/admin/payment/payment-causes"));
const PaymentDonations = React.lazy(() => import("@/pages/admin/payment/payment-donations"));
const PaymentIntentions = React.lazy(() => import("@/pages/admin/payment/payment-intentions"));
const PaymentWebhooks = React.lazy(() => import("@/pages/admin/payment/payment-webhooks"));
const PaymentTrace = React.lazy(() => import("@/pages/admin/payment/payment-trace"));
const PaymentRoutage = React.lazy(() => import("@/pages/admin/payment/payment-routage"));
const PaymentMarchands = React.lazy(() => import("@/pages/admin/payment/payment-marchands"));
const PaymentComptes = React.lazy(() => import("@/pages/admin/payment/payment-comptes"));


// Phase C V1.6 — Activités diocésaines (migration EDACI → economat)
// Phase C V1.6 — Contenu diocèse (migration EDACI → economat)


// --- SEDEC (Secrétariat d'État pour l'Enseignement Catholique) ---

// --- Plateforme de Paiement (Phase C V1.6) ---

function PageFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-brand-primary">
      <div className="flex flex-col items-center gap-3">
        <div className="text-2xl font-bold text-white">CATHO+ Économat</div>
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    </div>
  );
}

function Router() {
  return (
    <React.Suspense fallback={<PageFallback />}>
    <Switch>
      <Route path="/" component={LoginPage} />
      <Route path="/dashboard" component={TableauDeBordOuFlux} />
      <Route path="/flux" component={FluxFinanciers} />
      <Route path="/suivi-dons" component={SuiviDons} />
      <Route path="/rapprochement-jeko" component={RapprochementJeko} />
      <Route path="/suivi-dons/acces" component={SuiviDonsAcces} />
      <Route path="/mon-compte" component={MonCompte} />
      <Route path="/notifications" component={NotificationsPage} />
      <Route path="/calendrier/echeances" component={CalendrierEcheances} />
      <Route path="/tresorerie" component={TresoreriePage} />
      <Route path="/sante-paroisses" component={SanteParoissesPage} />
      <Route path="/cheques" component={ChequesPage} />
      <Route path="/rentabilite-directions" component={RentabiliteDirectionsPage} />
      <Route path="/perequation" component={PerequationPage} />
      <Route path="/comptabilite" component={ComptabilitePage} />
      <Route path="/comptabilite/plan-comptable" component={PlanComptable} />
      <Route path="/comptabilite/grand-livre" component={GrandLivre} />
      <Route path="/comptabilite/balance" component={BalanceGenerale} />
      <Route path="/comptabilite/bilan" component={BilanResultat} />
      <Route path="/budget" component={BudgetPrevisionnel} />
      <Route path="/comptabilite/rapprochement" component={RapprochementBancaire} />
      <Route path="/comptabilite/rapprochement-bancaire" component={RapprochementBancaire} />
      <Route path="/comptabilite/controles" component={ControlesComptablesPage} />
      <Route path="/comptabilite/pieces" component={PiecesComptablesPage} />
      <Route path="/cartographie" component={CartographiePage} />
      <Route path="/benchmarking" component={BenchmarkingPage} />
      <Route path="/clerge" component={ClergePage} />
      <Route path="/clerge/annuaire" component={ClergeAnnuaire} />
      <Route path="/clerge/affectations" component={ClergeAffectations} />
      <Route path="/clerge/parcours" component={ClergeParcours} />
      <Route path="/clerge/personnel-laic" component={PersonnelLaic} />
      <Route path="/clerge/formation" component={FormationRetraites} />
      <Route path="/clerge/:id" component={ClergeFiche} />
      <Route path="/conformite" component={ConformitePage} />
      <Route path="/projets/dpi" component={ProjetsDpiPage} />
      <Route path="/reversements" component={ReversementsPage} />
      <Route path="/caisse-versements" component={CaisseVersementsPage} />
      <Route path="/parametrage-comptable" component={ParametrageComptablePage} />
      <Route path="/parametrage-comptable/comptes-depot" component={ComptesDepotParoissesPage} />
      <Route path="/personnel-diocesain" component={PersonnelDiocesainPage} />
      <Route path="/projets" component={ProjetsPage} />
      <Route path="/observatoire" component={ObservatoirePage} />
      <Route path="/analytics" component={AnalyticsPage} />
      <Route path="/decisions" component={DecisionsPage} />
      <Route path="/communication" component={CommunicationPage} />
      <Route path="/communication/kit-qr" component={KitQrPage} />
      <Route path="/communication/kit-qr-ecoles" component={KitQrEcolesPage} />
      <Route path="/patrimoine" component={PatrimoinePage} />
      <Route path="/patrimoine/inventaire" component={PatrimoineInventaire} />
      <Route path="/patrimoine/travaux" component={PatrimoineTravaux} />
      <Route path="/patrimoine/assurances" component={PatrimoineAssurances} />
      <Route path="/patrimoine/contrats" component={PatrimoineContrats} />
      <Route path="/patrimoine/:id" component={PatrimoineFiche} />
      <Route path="/support" component={SupportPage} />
      <Route path="/paroisses" component={ParoissesPage} />
      <Route path="/paroisses/new" component={ParoisseFormPage} />
      <Route path="/paroisses/application" component={ParoissesApplicationPage} />
      <Route path="/paroisses/:id/edit" component={ParoisseFormPage} />
      <Route path="/paroisses/:id/dashboard" component={ParoisseDashboardPage} />
      <Route path="/paroisses/:id" component={ParoisseDetailPage} />
      <Route path="/cloture-hebdo" component={ClotureHebdoPage} />
      <Route path="/journal-transactions" component={JournalTransactionsPage} />
      <Route path="/transactions/new" component={TransactionFormPage} />
      <Route path="/transactions/:id" component={TransactionDetailPage} />
      <Route path="/reconciliation" component={ReconciliationPage} />
      <Route path="/integrations/mobile-money" component={MobileMoneyIntegrations} />
      <Route path="/integrations/api" component={ApiIntegrations} />
      <Route path="/integrations/webhooks" component={WebhookIntegrations} />
      <Route path="/rapports" component={RapportsPage} />
      <Route path="/rapports/financiers" component={RapportsFinanciersPage} />
      <Route path="/rapports/pastoraux" component={RapportsPastoraux} />
      <Route path="/rapports/export" component={RapportsExport} />
      <Route path="/tableau-personnalise" component={TableauPersonnalise} />
      {/* Catho Pay a fusionné avec l'espace Paiements (2026-07-12) : les
          anciennes URLs redirigent vers les pages équivalentes. */}
      <Route path="/catho-pay">{() => <Redirect to="/admin/payment" />}</Route>
      <Route path="/catho-pay/transactions">{() => <Redirect to="/admin/payment/donations" />}</Route>
      <Route path="/catho-pay/marchands">{() => <Redirect to="/admin/payment/marchands" />}</Route>
      <Route path="/catho-media" component={CathoMediaDashboard} />
      <Route path="/catho-media/publications" component={CathoMediaPublications} />
      <Route path="/catho-media/stats" component={CathoMediaStats} />
      
      {/* Liturgie & Sacrements Routes */}
      <Route path="/liturgie/calendrier" component={CalendrierLiturgique} />
      <Route path="/sacrements" component={Sacrements} />
      <Route path="/quetes-speciales" component={QuetesSpeciales} />
      <Route path="/dons-nature" component={DonsNature} />
      
      {/* Fournisseurs & Factures Routes */}
      <Route path="/fournisseurs" component={FournisseursPage} />
      <Route path="/factures-achats" component={FacturesAchatsPage} />
      <Route path="/gestion-dette" component={GestionDettePage} />
      <Route path="/messagerie" component={MessageriePage} />
      
      {/* Admin Routes */}
      <Route path="/admin/utilisateurs" component={AdminUtilisateurs} />
      <Route path="/admin/utilisateurs/:id" component={AdminUtilisateurDetail} />
      <Route path="/admin/roles" component={AdminRoles} />
      <Route path="/admin/audit" component={AdminAudit} />
      <Route path="/admin/archives-legales" component={AdminArchivesLegales} />
      <Route path="/admin/parametres" component={AdminParametres} />
      <Route path="/admin/vicariats" component={AdminVicariats} />
      <Route path="/admin/doyennes" component={AdminDoyennes} />
      <Route path="/admin/feature-flags" component={AdminFeatureFlags} />
      <Route path="/admin/documents" component={AdminDocuments} />
      <Route path="/admin/contributions" component={AdminContributions} />
      <Route path="/admin/dashboard-diocese" component={AdminDashboardDiocese} />
      {/* Phase C V1.6 — Activités diocésaines */}
      <Route path="/admin/evenements" component={AdminEvenements} />
      <Route path="/admin/annonces" component={AdminAnnonces} />
      {/* Chantiers DirCom 10-11/08 — grille radio + bulletin hebdomadaire */}
      <Route path="/admin/radio-programmes" component={AdminRadioProgrammes} />
      <Route path="/admin/semaine-diocesaine" component={AdminSemaineDiocesaine} />
      <Route path="/admin/pelerinages" component={AdminPelerinages} />
      <Route path="/admin/collectes" component={AdminCollectes} />
      <Route path="/admin/solidarite-projets" component={AdminSolidariteProjets} />

      {/* Contenu diocèse — Phase C V1.6 (actualités, newsletter, calendrier éditorial) */}
      <Route path="/admin/actualites" component={AdminActualites} />
      <Route path="/admin/actualites/editeur/:id?" component={AdminActualitesEditeur} />
      <Route path="/admin/newsletter" component={AdminNewsletter} />
      <Route path="/admin/calendrier-editorial" component={AdminCalendrierEditorial} />

      {/* Collectes & FDS Routes */}
      <Route path="/collectes/rapports" component={CollectesRapports} />
      <Route path="/fds/dashboard" component={FDSDashboard} />
      <Route path="/fds/paroisses" component={FDSParoisses} />
      <Route path="/fds/objectifs" component={FDSObjectifs} />
      <Route path="/denier-culte" component={DenierCulte} />
      <Route path="/intentions-messe" component={IntentionsMesse} />

      {/* CATHO+ App Routes */}
      <Route path="/catho-app" component={CathoAppDashboard} />
      <Route path="/catho-app/utilisateurs" component={CathoAppUsers} />
      <Route path="/catho-app/contenus" component={CathoAppContenus} />

      {/* Integration & Support Routes */}
      <Route path="/integrations/sync" component={IntegrationsSync} />
      <Route path="/integrations/backup" component={IntegrationsBackup} />
      <Route path="/visites-pastorales" component={VisitesPastorales} />
      <Route path="/formation-diocesaine" component={FormationDiocesaine} />
      <Route path="/rapports/annuel" component={RapportAnnuel} />
      <Route path="/rapports/canonique" component={RapportCanonique} />
      <Route path="/aide" component={AideDocumentation} />

      {/* SEDEC — Pilotage */}
      <Route path="/sedec/pilotage/dashboard" component={SedecPilotageDashboard} />
      <Route path="/sedec/pilotage/carte" component={SedecPilotageCarte} />
      <Route path="/sedec/pilotage/effectifs" component={SedecPilotageEffectifs} />
      <Route path="/sedec/pilotage/inscriptions" component={SedecPilotageInscriptions} />
      <Route path="/sedec/pilotage/performance" component={SedecPilotagePerformance} />
      <Route path="/sedec/pilotage/qualite" component={SedecPilotageQualite} />
      <Route path="/sedec/pilotage/gouvernance" component={SedecPilotageGouvernance} />
      <Route path="/sedec/pilotage/projet-educatif" component={SedecPilotageProjetEducatif} />
      <Route path="/sedec/pilotage/innovation" component={SedecPilotageInnovation} />
      <Route path="/sedec/pilotage/veille" component={SedecPilotageVeille} />
      <Route path="/sedec/pilotage/mena" component={SedecPilotageMena} />
      <Route path="/sedec/pilotage/stats" component={SedecPilotageStats} />
      <Route path="/sedec/pilotage/prospective" component={SedecPilotageProspective} />
      <Route path="/sedec/pilotage/rayonnement" component={SedecPilotageRayonnement} />
      <Route path="/sedec/pilotage/impact-social" component={SedecPilotageImpactSocial} />

      {/* SEDEC — Finances */}
      <Route path="/sedec/finances/consolidation" component={SedecFinancesConsolidation} />
      <Route path="/sedec/finances/revenus" component={SedecFinancesRevenus} />
      <Route path="/sedec/finances/paiements" component={SedecFinancesPaiements} />
      <Route path="/sedec/finances/impayes" component={SedecFinancesImpayes} />
      <Route path="/sedec/finances/frais" component={SedecFinancesFrais} />
      <Route path="/sedec/finances/bourses" component={SedecFinancesBourses} />
      <Route path="/sedec/finances/perequation" component={SedecFinancesPerequation} />
      <Route path="/sedec/finances/budgets" component={SedecFinancesBudgets} />
      <Route path="/sedec/finances/depenses" component={SedecFinancesDepenses} />
      <Route path="/sedec/finances/investissements" component={SedecFinancesInvestissements} />
      <Route path="/sedec/finances/analytique" component={SedecFinancesAnalytique} />
      <Route path="/sedec/finances/controle" component={SedecFinancesControle} />
      <Route path="/sedec/finances/achats-groupes" component={SedecFinancesAchatsGroupes} />
      <Route path="/sedec/finances/subventions" component={SedecFinancesSubventions} />
      <Route path="/sedec/finances/tresorerie" component={SedecFinancesTresorerie} />
      <Route path="/sedec/finances/assurances" component={SedecFinancesAssurances} />
      <Route path="/sedec/finances/rentabilite" component={SedecFinancesRentabilite} />
      <Route path="/sedec/finances/transparence" component={SedecFinancesTransparence} />
      <Route path="/sedec/finances/predictions" component={SedecFinancesPredictions} />
      <Route path="/sedec/finances/rapports" component={SedecFinancesRapports} />

      {/* SEDEC — Opérations */}
      <Route path="/sedec/operations/inscriptions-globales" component={SedecOperationsInscriptionsGlobales} />
      <Route path="/sedec/operations/matricules" component={SedecOperationsMatricules} />
      <Route path="/sedec/operations/affectations" component={SedecOperationsAffectations} />
      <Route path="/sedec/operations/parcours" component={SedecOperationsParcours} />
      <Route path="/sedec/operations/transferts" component={SedecOperationsTransferts} />
      <Route path="/sedec/operations/bourses-merite" component={SedecOperationsBoursesMerite} />
      <Route path="/sedec/operations/assurance-eleves" component={SedecOperationsAssuranceEleves} />
      <Route path="/sedec/operations/transport" component={SedecOperationsTransport} />
      <Route path="/sedec/operations/cantine" component={SedecOperationsCantine} />
      <Route path="/sedec/operations/activites" component={SedecOperationsActivites} />
      <Route path="/sedec/operations/sante" component={SedecOperationsSante} />
      <Route path="/sedec/operations/discipline" component={SedecOperationsDiscipline} />
      <Route path="/sedec/operations/orientation" component={SedecOperationsOrientation} />
      <Route path="/sedec/operations/examens" component={SedecOperationsExamens} />
      <Route path="/sedec/operations/communication" component={SedecOperationsCommunication} />

      {/* SEDEC — RH */}
      <Route path="/sedec/rh/annuaire" component={SedecRhAnnuaire} />
      <Route path="/sedec/rh/carrieres" component={SedecRhCarrieres} />
      <Route path="/sedec/rh/formation" component={SedecRhFormation} />
      <Route path="/sedec/rh/recrutement" component={SedecRhRecrutement} />
      <Route path="/sedec/rh/evaluations" component={SedecRhEvaluations} />
      <Route path="/sedec/rh/mobilite" component={SedecRhMobilite} />
      <Route path="/sedec/rh/conventions" component={SedecRhConventions} />
      <Route path="/sedec/rh/protection-sociale" component={SedecRhProtectionSociale} />
      <Route path="/sedec/rh/climat-social" component={SedecRhClimatSocial} />
      <Route path="/sedec/rh/competences" component={SedecRhCompetences} />
      <Route path="/sedec/rh/synergies" component={SedecRhSynergies} />
      <Route path="/sedec/rh/reconnaissance" component={SedecRhReconnaissance} />
      <Route path="/sedec/rh/prospective" component={SedecRhProspective} />
      <Route path="/sedec/rh/medecine" component={SedecRhMedecine} />
      <Route path="/sedec/rh/conflits" component={SedecRhConflits} />

      {/* SEDEC — Pastoral */}
      <Route path="/sedec/pastoral/projet" component={SedecPastoralProjet} />
      <Route path="/sedec/pastoral/aumoneries" component={SedecPastoralAumoneries} />
      <Route path="/sedec/pastoral/catechese" component={SedecPastoralCatechese} />
      <Route path="/sedec/pastoral/sacrements" component={SedecPastoralSacrements} />
      <Route path="/sedec/pastoral/mouvements" component={SedecPastoralMouvements} />
      <Route path="/sedec/pastoral/caritas" component={SedecPastoralCaritas} />
      <Route path="/sedec/pastoral/retraites" component={SedecPastoralRetraites} />
      <Route path="/sedec/pastoral/formation-humaine" component={SedecPastoralFormationHumaine} />
      <Route path="/sedec/pastoral/vocations" component={SedecPastoralVocations} />
      <Route path="/sedec/pastoral/celebrations" component={SedecPastoralCelebrations} />
      <Route path="/sedec/pastoral/ressources" component={SedecPastoralRessources} />
      <Route path="/sedec/pastoral/animateurs" component={SedecPastoralAnimateurs} />
      <Route path="/sedec/pastoral/impact" component={SedecPastoralImpact} />
      <Route path="/sedec/pastoral/synergie" component={SedecPastoralSynergie} />
      <Route path="/sedec/pastoral/rayonnement" component={SedecPastoralRayonnement} />

      {/* Plateforme de Paiement (Phase C V1.6) */}
      <Route path="/admin/payment" component={PaymentDashboard} />
      <Route path="/admin/payment/providers" component={PaymentProviders} />
      <Route path="/admin/payment/comptes" component={PaymentComptes} />
      <Route path="/admin/payment/operators" component={PaymentOperators} />
      <Route path="/admin/payment/causes" component={PaymentCauses} />
      <Route path="/admin/payment/donations" component={PaymentDonations} />
      <Route path="/admin/payment/intentions" component={PaymentIntentions} />
      <Route path="/admin/payment/webhooks" component={PaymentWebhooks} />
      <Route path="/admin/payment/trace" component={PaymentTrace} />
      <Route path="/admin/payment/routage" component={PaymentRoutage} />
      <Route path="/admin/payment/marchands" component={PaymentMarchands} />

      <Route component={NotFound} />
    </Switch>
    </React.Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <RouteGuard>
            <SectionGuard>
              <Router />
            </SectionGuard>
          </RouteGuard>
          {import.meta.env.DEV && <PagesRoadmap />}
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
